import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  CalendarX2,
  ChevronLeft,
  ChevronRight,
  List,
  Loader2,
  Plus,
} from 'lucide-react';
import {
  bookSlot,
  cancelLesson,
  fetchLessons,
  fetchSlots,
} from '../api/candidate';
import { useAuthStore } from '../store/auth';
import { CandidateSlot } from '../types';
import {
  CAL_END_HOUR,
  CAL_START_HOUR,
  durationLabel,
  gridPosition,
  monitorInitials,
  slotLabel,
} from '../lib/format';
import dayjs from '../lib/dates';
import ReserveModal from '../components/ReserveModal';

type View = 'liste' | 'calendrier';

export default function ReserveView() {
  const candidate = useAuthStore((s) => s.candidate);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<CandidateSlot | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [view, setView] = useState<View>('liste');
  const [weekOffset, setWeekOffset] = useState(0);

  const slotsQuery = useQuery({
    queryKey: ['candidate', 'slots'],
    queryFn: fetchSlots,
  });
  const lessonsQuery = useQuery({
    queryKey: ['candidate', 'lessons'],
    queryFn: fetchLessons,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['candidate', 'slots'] });
    void queryClient.invalidateQueries({ queryKey: ['candidate', 'lessons'] });
  };

  const bookMutation = useMutation({
    mutationFn: (slotId: string) => bookSlot(slotId),
    onSuccess: () => {
      invalidate();
      setSelected(null);
      setActionError(null);
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: (lessonId: string) => cancelLesson(lessonId),
    onSuccess: invalidate,
    onError: (err: Error) => setActionError(err.message),
  });

  // ── Semaine courante (lundi → dimanche) ─────────────────────────────────
  const weekStart = useMemo(
    () => dayjs().startOf('week').add(weekOffset, 'week'),
    [weekOffset],
  );
  const weekEnd = weekStart.add(7, 'day');
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')),
    [weekStart],
  );
  const inWeek = (iso: string) => {
    const d = dayjs(iso);
    return !d.isBefore(weekStart) && d.isBefore(weekEnd);
  };

  const allSlots = slotsQuery.data ?? [];
  const bookedLessons = (lessonsQuery.data ?? []).filter((l) => l.bookedOnline);
  const weekSlots = allSlots.filter((s) => inWeek(s.startsAt));
  const weekBooked = bookedLessons.filter((l) => inWeek(l.startsAt));
  const loading = slotsQuery.isLoading || lessonsQuery.isLoading;

  const openModal = (slot: CandidateSlot) => {
    setActionError(null);
    setSelected(slot);
  };

  const hours = Array.from(
    { length: CAL_END_HOUR - CAL_START_HOUR },
    (_, i) => CAL_START_HOUR + i,
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-slate-900">Réserver une leçon</h1>
          <p className="text-slate-600">
            Choisissez un créneau ouvert par votre auto-école — la réservation
            est confirmée instantanément.
          </p>
        </div>
        {/* Toggle Liste / Calendrier */}
        <div className="flex items-center bg-slate-100 p-1 rounded-lg self-start flex-shrink-0">
          {(
            [
              ['liste', 'Liste', List],
              ['calendrier', 'Calendrier', CalendarDays],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                view === key
                  ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-black/5'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation de semaine */}
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-3.5 shadow-sm flex items-center justify-between">
        <button
          onClick={() => setWeekOffset((w) => w - 1)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Semaine précédente"
        >
          <ChevronLeft size={18} className="text-slate-600" />
        </button>
        <div className="text-center">
          <div className="text-sm font-semibold text-slate-900">
            Semaine du {weekStart.format('D')} au{' '}
            {weekStart.add(6, 'day').format('D MMMM YYYY')}
          </div>
          <div className="text-xs text-slate-500">
            {weekSlots.length} créneau{weekSlots.length > 1 ? 'x' : ''} ouvert
            {weekSlots.length > 1 ? 's' : ''} à la réservation
          </div>
        </div>
        <button
          onClick={() => setWeekOffset((w) => w + 1)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Semaine suivante"
        >
          <ChevronRight size={18} className="text-slate-600" />
        </button>
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
          {actionError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Chargement des
          créneaux…
        </div>
      ) : view === 'liste' ? (
        /* ── Vue liste ──────────────────────────────────────────────── */
        <div className="space-y-6">
          {weekBooked.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-slate-900">Vos leçons réservées</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {weekBooked.map((lesson) => {
                  const isPast = new Date(lesson.endsAt).getTime() < Date.now();
                  const noShow = isPast && !lesson.honored;
                  return (
                  <div
                    key={lesson.id}
                    className={`rounded-2xl border p-5 shadow-sm flex items-center gap-4 ${
                      noShow
                        ? 'bg-red-50 border-red-200'
                        : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {monitorInitials(lesson.instructorName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">
                        {slotLabel(lesson.startsAt, lesson.endsAt)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {lesson.instructorName ?? 'Moniteur à confirmer'} ·{' '}
                        {durationLabel(lesson.startsAt, lesson.endsAt)} · Votre
                        leçon
                      </div>
                    </div>
                    {isPast ? (
                      // Leçon passée : statut de présence (émargement) au lieu d'annuler.
                      lesson.honored ? (
                        <span className="px-3 py-1.5 rounded-full bg-green-100 text-green-700 border border-green-300 text-xs font-semibold flex-shrink-0">
                          Présent(e)
                        </span>
                      ) : (
                        <span className="px-3 py-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-xs font-semibold flex-shrink-0">
                          Non honorée
                        </span>
                      )
                    ) : (
                      <button
                        onClick={() => {
                          setActionError(null);
                          cancelMutation.mutate(lesson.id);
                        }}
                        disabled={cancelMutation.isPending}
                        className="px-3.5 py-2 border border-green-300 text-green-800 hover:bg-green-100 disabled:opacity-60 text-sm font-medium rounded-lg transition-colors flex-shrink-0"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {weekBooked.length > 0 && (
              <h2 className="text-slate-900">Créneaux disponibles</h2>
            )}
            {weekSlots.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 shadow-sm flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <CalendarX2 className="text-slate-400" size={26} />
                </div>
                <h2 className="text-slate-900 mb-1">
                  Aucun créneau cette semaine
                </h2>
                <p className="text-sm text-slate-500 max-w-md">
                  Naviguez vers une autre semaine ou revenez plus tard : votre
                  auto-école ouvre régulièrement de nouveaux créneaux.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {weekSlots.map((slot) => (
                  <div
                    key={slot.id}
                    className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-center gap-4 transition-all hover:shadow-md"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {monitorInitials(slot.instructorName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">
                        {slotLabel(slot.startsAt, slot.endsAt)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {slot.instructorName ?? 'Moniteur à confirmer'} ·{' '}
                        {durationLabel(slot.startsAt, slot.endsAt)}
                      </div>
                    </div>
                    <button
                      onClick={() => openModal(slot)}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors flex-shrink-0"
                    >
                      Réserver
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── Vue calendrier ─────────────────────────────────────────── */
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              {/* En-tête des jours */}
              <div
                className="grid bg-slate-50 border-b border-slate-200"
                style={{ gridTemplateColumns: '56px repeat(7, minmax(0,1fr))' }}
              >
                <div />
                {days.map((d) => (
                  <div
                    key={d.toString()}
                    className="px-2 py-3 text-center border-l border-slate-100"
                  >
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {d.format('ddd')}
                    </div>
                    <div className="font-semibold text-slate-900">
                      {d.format('D')}
                    </div>
                  </div>
                ))}
              </div>

              {/* Corps : lignes horaires + créneaux positionnés */}
              <div
                className="grid relative"
                style={{
                  gridTemplateColumns: '56px repeat(7, minmax(0,1fr))',
                  gridAutoRows: '28px',
                }}
              >
                {hours.map((h, i) => (
                  <div
                    key={h}
                    className="text-[11px] text-slate-400 text-right pr-2 border-t border-slate-100"
                    style={{ gridColumn: 1, gridRow: `${i * 2 + 1} / span 2` }}
                  >
                    {String(h).padStart(2, '0')}:00
                  </div>
                ))}
                {/* Lignes de fond (séparateurs horaires) */}
                {hours.map((h, i) =>
                  days.map((_, dcol) => (
                    <div
                      key={`bg-${h}-${dcol}`}
                      className="border-t border-l border-slate-100"
                      style={{
                        gridColumn: dcol + 2,
                        gridRow: `${i * 2 + 1} / span 2`,
                      }}
                    />
                  )),
                )}

                {/* Leçons réservées */}
                {weekBooked.map((l) => {
                  const pos = gridPosition(l.startsAt, l.endsAt);
                  return (
                    <div
                      key={l.id}
                      className="relative z-10 m-0.5 rounded-lg border border-indigo-600 bg-indigo-600 text-white px-2 py-1 overflow-hidden shadow-sm"
                      style={{
                        gridColumn: pos.col,
                        gridRow: `${pos.row} / span ${pos.span}`,
                      }}
                      title={`Ma leçon · ${slotLabel(l.startsAt, l.endsAt)}`}
                    >
                      <div className="text-xs font-semibold truncate">
                        Ma leçon
                      </div>
                      <div className="text-[11px] text-indigo-100 truncate">
                        {dayjs(l.startsAt).format('HH:mm')} ·{' '}
                        {l.instructorName ?? '—'}
                      </div>
                    </div>
                  );
                })}

                {/* Créneaux disponibles */}
                {weekSlots.map((slot) => {
                  const pos = gridPosition(slot.startsAt, slot.endsAt);
                  return (
                    <button
                      key={slot.id}
                      onClick={() => openModal(slot)}
                      className="relative z-10 m-0.5 rounded-lg border-2 border-dashed border-green-400 bg-green-50/60 hover:bg-green-50 px-2 py-1 text-left overflow-hidden transition-colors"
                      style={{
                        gridColumn: pos.col,
                        gridRow: `${pos.row} / span ${pos.span}`,
                      }}
                      title={`Réserver — ${slotLabel(slot.startsAt, slot.endsAt)}`}
                    >
                      <div className="flex items-center gap-1 pointer-events-none">
                        <Plus size={12} className="text-green-600 flex-shrink-0" />
                        <span className="text-xs font-semibold text-green-700 truncate">
                          Réserver
                        </span>
                      </div>
                      <div className="text-[11px] text-green-600 truncate pointer-events-none">
                        {dayjs(slot.startsAt).format('HH:mm')} ·{' '}
                        {slot.instructorName ?? '—'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Légende */}
          <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded bg-indigo-600 border border-indigo-600" />
              <span className="text-xs text-slate-600">Ma leçon</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded bg-green-50 border-2 border-dashed border-green-400" />
              <span className="text-xs text-slate-600">Créneau disponible</span>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <ReserveModal
          slot={selected}
          schoolName={candidate?.school?.name ?? null}
          submitting={bookMutation.isPending}
          error={actionError}
          onConfirm={() => bookMutation.mutate(selected.id)}
          onClose={() => {
            setSelected(null);
            setActionError(null);
          }}
        />
      )}
    </div>
  );
}
